import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import RenewalBanner from '../components/RenewalBanner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiClient() {
  const token = localStorage.getItem('token');
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
}

const STATUS_STYLES = {
  active:    { bg: '#dcfce7', color: '#15803d', label: 'Active' },
  due:       { bg: '#fef3c7', color: '#92400e', label: 'Due' },
  overdue:   { bg: '#ffedd5', color: '#9a3412', label: 'Overdue' },
  lapsed:    { bg: '#fee2e2', color: '#dc2626', label: 'Lapsed' },
  cancelled: { bg: '#f1f5f9', color: '#64748b', label: 'Cancelled' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Main Component ────────────────────────────────────────────────────────────

function MemberDashboard() {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ full_name: '', phone: '', address: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [renewSuccess, setRenewSuccess] = useState('');

  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  // Admin-ish roles that reached this page via the member-view switch.
  const isStaff = ['tenantadmin', 'metaadmin', 'viewer'].includes(user.role);

  const loadMember = useCallback(() => {
    apiClient().get('/api/members/me')
      .then(res => {
        // Lapsed members must renew before accessing the dashboard
        if (res.data.status === 'lapsed') {
          window.location.href = '/renew';
          return;
        }
        setMember(res.data);
        setForm({ full_name: res.data.full_name, phone: res.data.phone || '', address: res.data.address || '' });
      })
      .catch(err => {
        if (err.response?.status === 401) { localStorage.clear(); window.location.href = '/login'; }
        else setError('Failed to load membership details.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadMember(); }, [loadMember]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveMsg('');
    try {
      await apiClient().put('/api/members/me', form);
      setMember(m => ({ ...m, ...form }));
      setEditMode(false); setSaveMsg('Details updated.');
    } catch { setSaveMsg('Failed to save. Please try again.'); }
    finally { setSaving(false); }
  };

  const toggleAutoRenew = async () => {
    const newVal = !member.is_auto_renew;
    try {
      await apiClient().put('/api/members/me/auto-renew', { is_auto_renew: newVal });
      setMember(m => ({ ...m, is_auto_renew: newVal }));
    } catch { setError('Failed to update auto-renew setting.'); }
  };

  const handleRenewalSuccess = (data) => {
    setRenewSuccess(`Membership renewed! New expiry: ${fmtDate(data.new_expiry_date)}`);
    loadMember();
  };

  const handleLogout = () => { localStorage.clear(); window.location.href = '/login'; };

  if (loading) return (
    <div className="auth-page"><div className="auth-card" style={{ textAlign: 'center' }}>
      <p style={{ color: '#6b7280' }}>Loading your membership...</p>
    </div></div>
  );

  if (error && !member) return (
    <div className="auth-page"><div className="auth-card" style={{ textAlign: 'center' }}>
      <p className="error-msg">{error}</p>
    </div></div>
  );

  const statusStyle = STATUS_STYLES[member?.status] || STATUS_STYLES.lapsed;
  const primaryColour = member?.primary_colour || '#1a1a2e';
  const secondaryColour = member?.secondary_colour || '#d4af37';

  return (
    <div className="dashboard">
      <div className="admin-navbar" style={{ background: primaryColour }}>
        <div className="admin-navbar-brand" style={{ color: secondaryColour }}>
          {member?.tenant_name || <><span style={{ color: '#1a1a2e', fontWeight: 'bold' }}>Me</span><span style={{ color: '#d4af37', fontWeight: 'bold' }}>Member</span></>}
        </div>
        <div className="admin-navbar-user">
          {isStaff && (
            <a className="m-viewswitch" href="/admin" title="Back to the administration dashboard">
              Admin view
            </a>
          )}
          <span>{user.full_name}</span>
          <button
            onClick={handleLogout}
            style={{
              border: '1.5px solid rgba(255,255,255,0.5)',
              borderRadius: '9999px',
              padding: '0.3rem 0.875rem',
              color: '#fff',
              background: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      <main className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>My Membership</h1>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Welcome back, {user.full_name}</p>
          </div>
          <span style={{
            background: statusStyle.bg, color: statusStyle.color,
            padding: '0.4rem 1rem', borderRadius: '9999px', fontWeight: 600, fontSize: '0.875rem',
          }}>
            {statusStyle.label}
          </span>
        </div>

        {renewSuccess && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '4px solid #16a34a',
            borderRadius: '8px', padding: '0.75rem 1rem', color: '#14532d',
            fontSize: '0.875rem', marginBottom: '1.25rem',
          }}>
            {renewSuccess}
          </div>
        )}

        <RenewalBanner member={member} onRenewSuccess={handleRenewalSuccess} />

        {/* Membership card */}
        <div style={{
          background: `linear-gradient(135deg, ${primaryColour}, ${primaryColour}cc)`,
          borderRadius: '12px', padding: '1.5rem', color: '#fff', marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Membership Number</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'monospace', color: secondaryColour }}>
                {member?.membership_number}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</div>
              <div style={{ fontWeight: 600 }}>{member?.membership_type_name || '—'}</div>
            </div>
          </div>
          <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)', display: 'flex', gap: '2rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Since</div>
              <div style={{ fontWeight: 600 }}>{fmtDate(member?.start_date)}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.25rem' }}>Expires</div>
              <div style={{ fontWeight: 600 }}>
                {member?.is_lifetime ? 'Lifetime' : fmtDate(member?.expiry_date)}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Auto-renew toggle */}
          <div className="card" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Auto-Renew</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {member?.is_auto_renew ? 'Automatically renew before expiry' : 'Manual renewal required'}
                </div>
              </div>
              <button onClick={toggleAutoRenew} style={{
                width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: member?.is_auto_renew ? secondaryColour : '#d1d5db', position: 'relative', transition: 'background 0.2s',
              }}>
                <span style={{
                  position: 'absolute', top: '3px', left: member?.is_auto_renew ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
                }} />
              </button>
            </div>
          </div>

          {/* Contact details */}
          <div className="card" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <div style={{ fontWeight: 600 }}>Contact Details</div>
              {!editMode && (
                <button onClick={() => setEditMode(true)} style={{ background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>Edit</button>
              )}
            </div>
            {editMode ? (
              <form onSubmit={handleSave}>
                <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.75rem' }}>Full Name</label>
                  <input name="full_name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.75rem' }}>Phone</label>
                  <input name="phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                </div>
                <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                  <label style={{ fontSize: '0.75rem' }}>Address</label>
                  <input name="address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="submit" disabled={saving} style={{ background: secondaryColour, color: primaryColour, border: 'none', borderRadius: '6px', padding: '0.4rem 0.875rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditMode(false)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', padding: '0.4rem 0.875rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                <div style={{ marginBottom: '0.3rem' }}><strong>Name:</strong> {member?.full_name}</div>
                <div style={{ marginBottom: '0.3rem' }}><strong>Email:</strong> {member?.email}</div>
                {member?.phone && <div style={{ marginBottom: '0.3rem' }}><strong>Phone:</strong> {member.phone}</div>}
                {member?.address && <div><strong>Address:</strong> {member.address}</div>}
              </div>
            )}
            {saveMsg && <p style={{ fontSize: '0.8rem', color: '#15803d', marginTop: '0.5rem' }}>{saveMsg}</p>}
          </div>
        </div>

        {/* Payment history */}
        {member?.payments?.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Payment History</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#6b7280', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {member.payments.map(p => (
                  <tr key={p.payment_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.75rem' }}>{new Date(p.payment_date).toLocaleDateString('en-AU')}</td>
                    <td style={{ padding: '0.75rem', textTransform: 'capitalize' }}>{p.payment_type}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>${parseFloat(p.amount).toFixed(2)} {p.currency}</td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default MemberDashboard;
